import { cva, type VariantProps } from 'class-variance-authority';
import type React from 'react';

import { cn } from '~/lib/cn';

// 'railway' variant is reserved for EN 45545 / rolling-stock contexts. Never use for general emphasis.
const buttonVariants = cva(
  // Base styles applied to every variant
  [
    'inline-flex items-center justify-center',
    'font-display font-semibold tracking-wide uppercase',
    'rounded-sm transition-colors duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] focus-visible:ring-offset-2',
    'disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none',
  ],
  {
    variants: {
      variant: {
        primary: [
          // Text uses pure white (#FFFFFF) rather than Frost White (#E8ECF4) to
          // achieve a contrast ratio of ≥4.5:1 against Electric Blue (#0062FF),
          // meeting WCAG 2 AA for normal-sized text.
          'bg-[var(--color-electric-blue)] text-white',
          // Hover: slightly lighter blue + subtle Cyan glow (low alpha so it is not distracting)
          'hover:bg-[#1a7aff] hover:shadow-[0_0_20px_-4px_rgba(0,98,255,0.7),0_0_28px_-6px_rgba(0,200,255,0.35)]',
        ],
        secondary: [
          'bg-transparent border border-[var(--color-cyan-signal)] text-[var(--color-cyan-signal)]',
          'hover:bg-[rgba(0,200,255,0.1)]',
        ],
        ghost: ['bg-transparent text-[var(--color-frost-white)]', 'hover:bg-[rgba(22,27,46,0.5)]'],
        // 'railway' variant is reserved for EN 45545 / rolling-stock contexts. Never use for general emphasis.
        railway: [
          'bg-[var(--color-rail-orange)] text-[var(--color-void-navy)]',
          'hover:bg-[#ff824a] hover:shadow-[0_0_20px_-4px_rgba(255,107,43,0.6)]',
        ],
      },
      size: {
        sm: 'h-9 px-4 text-sm',
        md: 'h-11 px-6 text-base',
        lg: 'h-14 px-8 text-lg',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
);

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'railway';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonBaseProps extends VariantProps<typeof buttonVariants> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
  children?: React.ReactNode;
}

type ButtonAsButton = ButtonBaseProps &
  Omit<React.ComponentPropsWithoutRef<'button'>, keyof ButtonBaseProps> & {
    href?: undefined;
  };

type ButtonAsAnchor = ButtonBaseProps &
  Omit<React.ComponentPropsWithoutRef<'a'>, keyof ButtonBaseProps> & {
    href: string;
  };

type ButtonProps = ButtonAsButton | ButtonAsAnchor;

function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
  children,
  href,
  ...rest
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size, fullWidth }), className);

  if (href !== undefined) {
    return (
      <a href={href} className={classes} {...(rest as React.ComponentPropsWithoutRef<'a'>)}>
        {children}
      </a>
    );
  }

  return (
    <button className={classes} {...(rest as React.ComponentPropsWithoutRef<'button'>)}>
      {children}
    </button>
  );
}

export { Button, buttonVariants };
export type { ButtonProps, ButtonVariant, ButtonSize };
export default Button;
